/**
 * Compliance Documents View
 * Platform-tenant policy library with versioned publish + owner acknowledgment.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../common/Icons';
import { complianceDocumentsService } from '../../services/api';
import './ComplianceDocumentsView.css';

const EMPTY_FORM = {
  title: '',
  slug: '',
  purpose: 'policy',
  audience: 'owner',
  body_text: '',
};

const PURPOSE_LABELS = {
  policy: 'Policy',
  code_of_conduct: 'Code of conduct',
  aup: 'Acceptable use',
  other: 'Other',
};

const AUDIENCE_LABELS = {
  owner: 'Owner (System Admin)',
  external_accountant: 'External accountants',
};

const ACK_DECLARATION =
  'I have read this document in the form shown and acknowledge that I understand it. I am signing as the person logged into this account.';

const ComplianceDocumentsView = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [acks, setAcks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [typedName, setTypedName] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const listRes = await complianceDocumentsService.getAll();
      setDocuments(listRes.data || []);
    } catch (err) {
      console.error('Failed to load compliance documents:', err);
      setError('Failed to load compliance documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyDetail = (data) => {
    setDetail(data);
    const body = data.draft?.body_text ?? data.published?.body_text ?? '';
    setForm({
      title: data.title || '',
      slug: data.slug || '',
      purpose: data.purpose || 'policy',
      audience: data.audience || 'owner',
      body_text: body,
    });
    setAccepted(false);
    setTypedName('');
  };

  const selectDocument = async (id) => {
    setIsNew(false);
    setSelectedId(id);
    setError(null);
    try {
      const [docRes, ackRes] = await Promise.all([
        complianceDocumentsService.getById(id),
        complianceDocumentsService.listAcknowledgments(id),
      ]);
      applyDetail(docRes.data);
      setAcks(ackRes.data || []);
    } catch (err) {
      console.error('Failed to load document:', err);
      setError(err.response?.data?.message || 'Failed to load document');
    }
  };

  const startNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setDetail(null);
    setAcks([]);
    setForm({ ...EMPTY_FORM });
    setAccepted(false);
    setTypedName('');
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (isNew) {
        const created = await complianceDocumentsService.create(form);
        await load();
        if (created.data?.id) {
          await selectDocument(created.data.id);
        }
      } else if (selectedId) {
        const payload = {
          title: form.title,
          purpose: form.purpose,
          audience: form.audience,
          body_text: form.body_text,
        };
        if (!detail?.published) payload.slug = form.slug;
        const updated = await complianceDocumentsService.update(selectedId, payload);
        applyDetail(updated.data);
        await load();
      }
    } catch (err) {
      console.error('Failed to save document:', err);
      setError(err.response?.data?.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedId || !window.confirm('Publish this version? The text will freeze. Later edits create a new draft.')) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const updated = await complianceDocumentsService.publish(selectedId);
      applyDetail(updated.data);
      await load();
    } catch (err) {
      console.error('Failed to publish:', err);
      setError(err.response?.data?.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      setError(null);
      await complianceDocumentsService.acknowledge(selectedId, {
        typed_name: typedName,
        accepted: true,
      });
      await selectDocument(selectedId);
      await load();
    } catch (err) {
      console.error('Failed to acknowledge:', err);
      setError(err.response?.data?.message || 'Failed to record acknowledgment');
    } finally {
      setSaving(false);
    }
  };

  const published = detail?.published;
  const draft = detail?.draft;
  const canPublish = !isNew && selectedId && draft;
  const isOwnerAudience = (form.audience || detail?.audience || 'owner') === 'owner';
  const showAck = !isNew && published && isOwnerAudience && !detail?.acknowledged_current;
  const showSignoff =
    showAck
    || (!isNew && selectedId && isOwnerAudience && detail?.acknowledged_current)
    || (!isNew && selectedId && acks.length > 0);

  return (
    <div className="compliance-page">
      <div className="compliance-toolbar">
        <p>
          Versioned policy library. Owner docs are signed here. External-accountant docs gate the Accountant Portal only.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={startNew}>
          <Icons.Plus size={16} />
          New document
        </button>
      </div>

      {error && (
        <div className="alert alert-warning" style={{ flexShrink: 0 }}>
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="compliance-workspace">
        <div className="card compliance-list">
          <div className="compliance-list-scroll">
            {loading ? (
              <div className="empty-state" style={{ padding: '32px' }}>
                <Icons.Loader size={32} className="spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="empty-state" style={{ padding: '32px' }}>
                <p>No documents yet</p>
              </div>
            ) : (
              documents.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => selectDocument(doc.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 16px',
                    border: 'none',
                    borderBottom: '1px solid var(--border-color)',
                    background: selectedId === doc.id ? 'var(--bg-secondary)' : 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{doc.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {PURPOSE_LABELS[doc.purpose] || doc.purpose}
                    {doc.audience === 'external_accountant' ? ' · accountants' : ''}
                    {doc.published_version_n ? ` · v${doc.published_version_n}` : ' · unpublished'}
                    {doc.draft_version_n ? ' · draft' : ''}
                    {doc.audience === 'external_accountant'
                      ? (doc.ack_count ? ` · ${doc.ack_count} signed` : '')
                      : (doc.acknowledged_current ? ' · signed' : '')}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="card compliance-editor">
          <div className="compliance-editor-scroll">
            {!isNew && !selectedId ? (
              <div className="empty-state">
                <Icons.Shield size={40} />
                <p>Select a document or create a new one</p>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Information security policy"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Slug</label>
                    <input
                      className="form-control"
                      value={form.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      placeholder="information-security-policy"
                      disabled={!!published}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Purpose</label>
                    <select
                      className="form-control"
                      value={form.purpose}
                      onChange={(e) => handleChange('purpose', e.target.value)}
                    >
                      <option value="policy">Policy</option>
                      <option value="code_of_conduct">Code of conduct</option>
                      <option value="aup">Acceptable use</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Who must sign</label>
                    <select
                      className="form-control"
                      value={form.audience}
                      onChange={(e) => handleChange('audience', e.target.value)}
                    >
                      {Object.entries(AUDIENCE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {form.audience === 'external_accountant' && (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 0 }}>
                    Publishing gates the Accountant Portal until each firm person acknowledges the current version. Farm staff are not asked.
                  </p>
                )}

                {published && (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 0 }}>
                    Published v{published.version_n}
                    {' · '}
                    sha256 {published.content_sha256}
                    {draft ? ' · editing a new draft' : ''}
                  </p>
                )}

                <div className="form-group">
                  <label className="form-label">Body</label>
                  <textarea
                    className="form-control"
                    rows={16}
                    value={form.body_text}
                    onChange={(e) => handleChange('body_text', e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving || !form.title.trim() || !form.body_text.trim()}
                  >
                    {saving ? 'Saving...' : isNew ? 'Create draft' : 'Save draft'}
                  </button>
                  {canPublish && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePublish}
                      disabled={saving}
                    >
                      <Icons.CheckCircle size={16} />
                      Publish version
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showSignoff && (
        <div className="card compliance-signoff">
          {showAck && (
            <>
              <h3>Acknowledge published v{published.version_n}</h3>
              <p style={{ color: 'var(--text-muted)' }}>
                You are signing the frozen text (sha256 {published.content_sha256}), not any unsaved draft.
              </p>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '12px' }}>
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span>{detail?.declaration_text || ACK_DECLARATION}</span>
              </label>
              <div className="compliance-signoff-actions">
                <div className="form-group">
                  <label className="form-label">Typed legal name</label>
                  <input
                    className="form-control"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Your full legal name"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAcknowledge}
                  disabled={saving || !accepted || !typedName.trim()}
                >
                  Record acknowledgment
                </button>
              </div>
            </>
          )}

          {!isNew && selectedId && isOwnerAudience && detail?.acknowledged_current && (
            <div className="alert alert-success">
              <Icons.CheckCircle size={18} />
              <span>You have acknowledged the current published version.</span>
            </div>
          )}

          {!isNew && selectedId && acks.length > 0 && (
            <>
              <h3>Acknowledgments</h3>
              <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Signed</th>
                    <th>Name</th>
                    <th>Signer</th>
                    <th>Version</th>
                    <th>SHA-256</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {acks.map((row) => (
                    <tr key={row.id}>
                      <td>{row.signed_at ? new Date(row.signed_at).toLocaleString() : ''}</td>
                      <td>{row.typed_name}</td>
                      <td>{row.platform_user_id ? 'Accountant' : 'Owner'}</td>
                      <td>v{row.version_n}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.content_sha256}</td>
                      <td>{row.ip_address || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ComplianceDocumentsView;
